import { useState, useMemo } from "react";
import { usePagination } from "../../../../hooks/usePagination";
import { COLLECTIONS } from "../../../../services/collections";
import { PAGE_SIZE } from "../constants";
import { FINANCIAL_STATUS, FINANCIAL_TYPES } from "../../../../constants/financial";

const today = () => new Date().toISOString().split("T")[0];

export const useReceivablesRecords = (idTenant) => {
    // Filtros Temporários (para edição em tela)
    const [tempStartDate, setTempStartDate] = useState(today());
    const [tempEndDate, setTempEndDate] = useState(today());

    // Filtros Aplicados (que disparam a busca no Firestore)
    const [appliedStartDate, setAppliedStartDate] = useState("");
    const [appliedEndDate, setAppliedEndDate] = useState("");

    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");

    // Filtros do Firestore — baseados nas datas aplicadas
    const firestoreFilters = useMemo(() => {
        if (!appliedStartDate || !appliedEndDate) return [];
        const filters = [
            { field: "type", operator: "==", value: FINANCIAL_TYPES.ASSET },
            { field: "dueDate", operator: ">=", value: appliedStartDate },
            { field: "dueDate", operator: "<=", value: appliedEndDate },
        ];
        if (statusFilter) {
            filters.push({ field: "status", operator: "==", value: statusFilter });
        }
        return filters;
    }, [statusFilter, appliedStartDate, appliedEndDate]);

    // Função para aplicar a busca
    const handleSearch = () => {
        setAppliedStartDate(tempStartDate);
        setAppliedEndDate(tempEndDate);
    };

    // Paginação com filtros de data
    const {
        data: records,
        loading,
        error,
        hasMore,
        loadMore,
        refresh,
    } = usePagination(
        `tenants/${idTenant}/${COLLECTIONS.FINANCIAL_RECORDS}`,
        idTenant,
        {
            pageSize: PAGE_SIZE,
            orderField: "dueDate",
            orderDirection: "desc",
            filters: firestoreFilters,
            skip: !appliedStartDate || !appliedEndDate,
        }
    );

    // Filtragem local por busca textual
    const filteredRecords = useMemo(() => {
        if (!search.trim()) return records;
        const term = search.toLowerCase().trim();
        return records.filter(r =>
            (r.memberName || "").toLowerCase().includes(term) ||
            (r.description || "").toLowerCase().includes(term) ||
            (r.entityName || "").toLowerCase().includes(term) ||
            (r.saleNumber || "").toLowerCase().includes(term)
        );
    }, [records, search]);

    // Totais do período carregado
    const summary = useMemo(() => {
        let totalPending = 0;
        let totalOverdue = 0;
        let totalPaid = 0;
        let countPending = 0;
        let countOverdue = 0;

        records.forEach(r => {
            const netValue = r.netAmount || r.grossAmount || 0;

            if (r.status === FINANCIAL_STATUS.PENDING) {
                totalPending += netValue;
                countPending++;
            } else if (r.status === FINANCIAL_STATUS.OVERDUE) {
                totalOverdue += netValue;
                countOverdue++;
            } else if (r.status === FINANCIAL_STATUS.PAID) {
                // Pagos baseiam-se sempre em netAmount, com fallback gross
                totalPaid += netValue;
            }
        });

        return { totalPending, totalOverdue, totalPaid, countPending, countOverdue };
    }, [records]);

    return {
        records: filteredRecords,
        allRecords: records,
        summary,
        loading,
        error,
        hasMore,
        loadMore,
        refresh,
        statusFilter, setStatusFilter,
        search, setSearch,
        startDate: tempStartDate,
        setStartDate: setTempStartDate,
        endDate: tempEndDate,
        setEndDate: setTempEndDate,
        handleSearch,
        hasSearched: !!(appliedStartDate && appliedEndDate),
    };
};
